import type {
  TBillingRecord,
  TInvoiceCompany,
  TInvoiceCustomer,
} from '@/types/subscription';
import type { CSSProperties } from 'react';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { renderToStaticMarkup } from 'react-dom/server';
import { useIntl, IntlProvider, FormattedMessage } from 'react-intl';

import ar from '@/locales/ar.json';
import en from '@/locales/en.json';
import { useSettingsStore } from '@/store/useSettingsStore';
import { flattenMessages } from '@/utils';

const COLOR_PRIMARY = '#181B25';
const COLOR_SECONDARY = '#525866';
const COLOR_BORDER = '#E1E4EA';
const COLOR_BORDER_LIGHT = '#F2F5F8';
const COLOR_TABLE_HEAD = '#F2F5F8';
const FONT_FAMILY = 'Aptos, Helvetica, Arial, sans-serif';
const FONT_SIZE_BASE = '14px';
const LINE_HEIGHT_BASE = '20px';
const SPACE_BETWEEN: CSSProperties['justifyContent'] = 'space-between';
const TEXT_ALIGN_END: CSSProperties['textAlign'] = 'end';
const I18N_KEY_PREFIX = 'settings.subscription.invoice-template';

const containerStyle: CSSProperties = {
  width: '794px',
  minHeight: '1123px',
  backgroundColor: '#FFFFFF',
  padding: '40px',
  fontFamily: FONT_FAMILY,
  color: COLOR_PRIMARY,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
};

const headerRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: SPACE_BETWEEN,
  alignItems: 'flex-start',
  paddingBottom: '24px',
};

const brandLogoStyle: CSSProperties = {
  display: 'block',
  width: '140px',
  height: 'auto',
};

const invoiceTitleStyle: CSSProperties = {
  fontSize: '32px',
  fontWeight: 700,
  lineHeight: '40px',
  letterSpacing: '-0.32px',
  textAlign: TEXT_ALIGN_END,
  textTransform: 'uppercase',
};

const metaWrapStyle: CSSProperties = {
  marginTop: '8px',
  fontSize: FONT_SIZE_BASE,
  lineHeight: '24px',
  textAlign: TEXT_ALIGN_END,
};

const metaLabelStyle: CSSProperties = { color: COLOR_SECONDARY };

const dividerStyle: CSSProperties = {
  height: '1px',
  backgroundColor: COLOR_BORDER_LIGHT,
  width: '100%',
};

const addressRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: SPACE_BETWEEN,
  paddingTop: '24px',
  paddingBottom: '24px',
};

const addressLabelStyle: CSSProperties = {
  fontSize: FONT_SIZE_BASE,
  fontWeight: 600,
  lineHeight: LINE_HEIGHT_BASE,
};

const addressNameStyle: CSSProperties = {
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: '28px',
  letterSpacing: '-0.2px',
  marginTop: '4px',
};

const addressLineStyle: CSSProperties = {
  fontSize: FONT_SIZE_BASE,
  lineHeight: LINE_HEIGHT_BASE,
  marginTop: '4px',
};

const billToColStyle: CSSProperties = { textAlign: TEXT_ALIGN_END };

const mailtoLinkStyle: CSSProperties = {
  color: 'inherit',
  textDecoration: 'none',
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '24px',
};

const tableHeadCellStyle: CSSProperties = {
  backgroundColor: COLOR_TABLE_HEAD,
  color: COLOR_SECONDARY,
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: LINE_HEIGHT_BASE,
  textAlign: 'start',
  padding: '12px 16px',
};

const tableBodyCellStyle: CSSProperties = {
  fontSize: '13px',
  lineHeight: LINE_HEIGHT_BASE,
  padding: '16px',
  borderBottom: `1px solid ${COLOR_BORDER}`,
  verticalAlign: 'middle',
};

const periodCellStyle: CSSProperties = {
  ...tableBodyCellStyle,
  whiteSpace: 'nowrap',
};

const totalCellStyle: CSSProperties = {
  ...tableBodyCellStyle,
  fontSize: '16px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const footerStyle: CSSProperties = {
  marginTop: 'auto',
  paddingTop: '32px',
  textAlign: 'center',
  fontSize: FONT_SIZE_BASE,
  lineHeight: '24px',
  color: COLOR_SECONDARY,
};

interface IInvoiceTemplateProps {
  record: TBillingRecord;
  company: TInvoiceCompany;
  customer: TInvoiceCustomer;
}

const InvoiceTemplate = ({
  record,
  company,
  customer,
}: IInvoiceTemplateProps) => {
  const intl = useIntl();
  const dir = intl.locale === 'ar' ? 'rtl' : 'ltr';
  const t = (key: string, values?: Record<string, string>) =>
    intl.formatMessage({ id: `${I18N_KEY_PREFIX}.${key}` }, values);

  return (
    <div dir={dir} style={containerStyle}>
      <div style={headerRowStyle}>
        <img
          src="/assets/svg/tir-tir.svg"
          alt={company.name}
          style={brandLogoStyle}
        />
        <div>
          <div style={invoiceTitleStyle}>{t('title')}</div>
          <div style={metaWrapStyle}>
            <div>
              <span style={metaLabelStyle}>{t('invoice-date')}: </span>
              <span>{record.date}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={dividerStyle} />

      <div style={addressRowStyle}>
        <div>
          <div style={addressLabelStyle}>{t('from')}</div>
          <div style={addressNameStyle}>{company.name}</div>
          <div style={addressLineStyle}>
            <a href={`mailto:${company.email}`} style={mailtoLinkStyle}>
              {company.email}
            </a>
          </div>
          <div style={addressLineStyle}>{company.phone}</div>
        </div>
        <div style={billToColStyle}>
          <div style={addressLabelStyle}>{t('bill-to')}</div>
          <div style={addressNameStyle}>{customer.name}</div>
          <div style={addressLineStyle}>
            <a href={`mailto:${customer.email}`} style={mailtoLinkStyle}>
              {customer.email}
            </a>
          </div>
        </div>
      </div>

      <div style={dividerStyle} />

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={tableHeadCellStyle}>{t('description')}</th>
            <th style={{ ...tableHeadCellStyle, whiteSpace: 'nowrap' }}>
              {t('period')}
            </th>
            <th style={tableHeadCellStyle}>{t('students')}</th>
            <th style={{ ...tableHeadCellStyle, whiteSpace: 'nowrap' }}>
              {t('unit-price')}
            </th>
            <th style={{ ...tableHeadCellStyle, whiteSpace: 'nowrap' }}>
              {t('amount')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tableBodyCellStyle}>{record.invoice}</td>
            <td style={periodCellStyle}>{record.billingPeriod}</td>
            <td style={tableBodyCellStyle}>{record.students}</td>
            <td style={periodCellStyle}>{record.amount}</td>
            <td style={periodCellStyle}>{record.amount}</td>
          </tr>
          <tr>
            <td style={tableBodyCellStyle} />
            <td style={tableBodyCellStyle} />
            <td style={tableBodyCellStyle} />
            <td style={totalCellStyle}>{t('total')}</td>
            <td style={totalCellStyle}>{record.amount}</td>
          </tr>
        </tbody>
      </table>

      <div style={footerStyle}>
        <div>{t('thank-you')}</div>
        <div>
          <FormattedMessage
            id={`${I18N_KEY_PREFIX}.support`}
            values={{
              email: (
                <a href={`mailto:${company.email}`} style={mailtoLinkStyle}>
                  {company.email}
                </a>
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
};

const TEMPLATE_PX_WIDTH = 794;
const A4_PT = { width: 595.28, height: 841.89 };
const CAPTURE_SCALE = 2;
const MESSAGES_MAP: Record<string, Record<string, unknown>> = { en, ar };

const waitForLayout = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

const resolveLocale = () => {
  const language = useSettingsStore.getState().settings.language;
  const locale = MESSAGES_MAP[language] ? language : 'en';
  const messages = flattenMessages(MESSAGES_MAP[locale]);
  return { locale, messages };
};

const renderTemplateToHiddenNode = (
  record: TBillingRecord,
  company: TInvoiceCompany,
  customer: TInvoiceCustomer,
  locale: string,
  messages: Record<string, string>
): HTMLDivElement => {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-99999px';
  container.style.top = '0';
  container.style.width = `${TEMPLATE_PX_WIDTH}px`;
  container.style.background = '#FFFFFF';
  container.style.pointerEvents = 'none';
  container.innerHTML = renderToStaticMarkup(
    <IntlProvider locale={locale} messages={messages} defaultLocale="en">
      <InvoiceTemplate record={record} company={company} customer={customer} />
    </IntlProvider>
  );
  document.body.appendChild(container);
  return container;
};

const captureRecordImage = async (
  record: TBillingRecord,
  company: TInvoiceCompany,
  customer: TInvoiceCustomer,
  locale: string,
  messages: Record<string, string>
): Promise<{ dataUrl: string; widthPx: number; heightPx: number }> => {
  const container = renderTemplateToHiddenNode(
    record,
    company,
    customer,
    locale,
    messages
  );
  try {
    await waitForLayout();
    const canvas = await html2canvas(container, {
      backgroundColor: '#FFFFFF',
      scale: CAPTURE_SCALE,
      useCORS: true,
      logging: false,
      windowWidth: TEMPLATE_PX_WIDTH,
    });
    return {
      dataUrl: canvas.toDataURL('image/png'),
      widthPx: canvas.width / CAPTURE_SCALE,
      heightPx: canvas.height / CAPTURE_SCALE,
    };
  } finally {
    container.remove();
  }
};

const addRecordToDoc = async (
  doc: jsPDF,
  record: TBillingRecord,
  company: TInvoiceCompany,
  customer: TInvoiceCustomer,
  locale: string,
  messages: Record<string, string>
) => {
  const { dataUrl, widthPx, heightPx } = await captureRecordImage(
    record,
    company,
    customer,
    locale,
    messages
  );
  const pxToPt = A4_PT.width / widthPx;
  const imgWidthPt = A4_PT.width;
  const imgHeightPt = heightPx * pxToPt;
  doc.addImage(dataUrl, 'PNG', 0, 0, imgWidthPt, imgHeightPt);
};

// eslint-disable-next-line react-refresh/only-export-components
export const generateInvoicesPdf = async (
  records: TBillingRecord[],
  company: TInvoiceCompany,
  customer: TInvoiceCustomer
): Promise<Blob> => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const { locale, messages } = resolveLocale();

  for (let i = 0; i < records.length; i += 1) {
    if (i > 0) doc.addPage();
    await addRecordToDoc(doc, records[i], company, customer, locale, messages);
  }

  return doc.output('blob');
};

export default InvoiceTemplate;
